'use client';

/**
 * ClubesGrid — grid responsivo de ClubCardFacelift + botão "Carregar mais".
 *
 * Receives `clubs[]` controlled externally (ClubesView). Quando `hasMore`,
 * mostra o botão de paginação no rodapé. `isPending` desabilita botão e
 * mostra loader visual durante a Server Action.
 */
import * as React from 'react';
import { motion } from 'framer-motion';

import { Button } from '@brisa/ui/components/ui/button';
import { ClubCardFacelift } from '@brisa/ui/components/facelift/club-card/ClubCardFacelift';
import type { ClubListItem } from '@brisa/clubs';

export interface ClubesGridCopy {
  loadMore: string;
  loading: string;
  /** Empty-state copy é consumido pelo ClubesView (cobre list+map). */
  emptyTitle: string;
  emptyBody: string;
  emptyClear: string;
}

export interface ClubesGridProps {
  clubs: ClubListItem[];
  hasMore: boolean;
  isPending: boolean;
  onLoadMore: () => void;
  copy: ClubesGridCopy;
}

export function ClubesGrid({
  clubs,
  hasMore,
  isPending,
  onLoadMore,
  copy,
}: ClubesGridProps) {
  return (
    <div data-slot="clubes-grid" className="flex flex-col gap-12">
      {/* Flex+wrap com width explícita por viewport ao invés de Grid:
          Grid items com `min-width: auto` default crescem pelo content min
          (nome longo do clube). `min-w-0` em grid items NÃO é suficiente em
          todos browsers/Next configs. Width explícita via calc() garante 25%
          matemático preciso e idêntico entre filtros. (Bruno 2026-05-16) */}
      <ul className="flex w-full flex-wrap gap-6">
        {clubs.map((club, idx) => (
          <motion.li
            key={club.slug}
            className="w-full shrink-0 sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)]"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              ease: 'easeOut',
              // Base 0.3s — espera o h1 (Reveal delay 0) e o FilterBar (Reveal
              // delay 0.1) entrarem antes pra escalonar leitura e performance.
              delay: 0.3 + Math.min(idx * 0.04, 0.6),
            }}
          >
            <ClubCardFacelift
              club={{
                slug: club.slug,
                name: club.name,
                neighborhood: club.neighborhood,
                cover_url: club.photos?.[0]?.url ?? null,
                avatar_url: club.photos?.[0]?.url ?? null,
                integration_status: club.integration_status,
                vibe: club.vibe?.[0],
              }}
              href={`/clubes/${club.slug}`}
              dropShadow={false}
              data-event="club-card-clicked"
              data-club-slug={club.slug}
            />
          </motion.li>
        ))}
      </ul>

      {hasMore && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary-pill"
            size="md"
            onClick={onLoadMore}
            disabled={isPending}
            aria-busy={isPending || undefined}
            data-event="clubes-load-more"
          >
            {isPending ? copy.loading : copy.loadMore}
          </Button>
        </div>
      )}
    </div>
  );
}
